/**
 * The first node (Feature 80): the capability every template starts from,
 * and how it starts. A draft without a first capability has no first node
 * yet (D2), and Save waits for one (D20); a schedule's first task runs with
 * the fixed input, so a required input field without a value holds Save
 * too (D5, D15), as does a schedule whose sentence makes no cron, or whose
 * custom cron is not five fields (Feature 81 D13). Removing the first node, while the template is unsaved,
 * takes its links and leaves the gates after it following nothing (D3).
 */
import type { LayoutPoint, LinkageProblem } from '@optum/api-client/types';

import { isValidCron } from '../../lib/cron.js';
import { FIRST, type Draft } from './draft.js';
import { mappableFields, type Schemas } from './references.js';
import { isSchedule } from './start.js';

/** Whether the draft has its first node: a first capability. */
export function hasFirst(draft: Draft): boolean {
  return draft.firstCapabilityId !== '';
}

/** Why a schedule's cron holds Save: none yet, or not five cron fields. */
function cronProblems(cron: string | null | undefined): LinkageProblem[] {
  const code = 'schedule_cron_missing';
  if (!cron?.trim()) return [{ where: FIRST, code, message: 'set when it runs' }];
  if (!isValidCron(cron)) {
    return [{ where: FIRST, code, message: 'when it runs is not five cron fields' }];
  }
  return [];
}

/** What Save waits on for the first node: none yet, or a schedule's cron or required input missing. */
export function firstProblems(draft: Draft, schemas: Schemas): LinkageProblem[] {
  if (!hasFirst(draft)) {
    return [{ where: FIRST, code: 'first_missing', message: 'add the first node' }];
  }
  if (!isSchedule(draft.start)) return [];
  const given = draft.start.input ?? {};
  const inputs = mappableFields(schemas.get(draft.firstCapabilityId))
    .filter((field) => field.required && !(field.name in given))
    .map((field) => ({
      where: FIRST,
      code: 'schedule_input_missing',
      message: `${field.name} is required and has no value`,
    }));
  return [...cronProblems(draft.start.cron), ...inputs];
}

/** The first node removed: its capability, its links, its position; the gates after it follow nothing. */
export function removeFirst(draft: Draft): Draft {
  const layout = { ...draft.layout };
  delete layout[FIRST];
  return {
    ...draft,
    firstCapabilityId: '',
    links: draft.links.filter((l) => l.from !== FIRST),
    decisions: draft.decisions.map((d) => (d.from === FIRST ? { ...d, from: '' } : d)),
    layout,
  };
}

/** One field of a schedule's fixed input set, or cleared when its value is empty (D5). */
export function setStartInput(draft: Draft, field: string, value: unknown): Draft {
  const input: Record<string, unknown> = { ...(draft.start.input ?? {}) };
  if (value === '' || value === undefined) delete input[field];
  else input[field] = value;
  return { ...draft, start: { ...draft.start, input } };
}

/** The first node added from the toolbar: its capability, placed at `at` (D2). */
export function addFirst(draft: Draft, capabilityId: string, at: LayoutPoint): Draft {
  return { ...draft, firstCapabilityId: capabilityId, layout: { ...draft.layout, [FIRST]: at } };
}
